/* eslint-disable @angular-eslint/no-output-native */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { SearchService } from '../../../pages/search/services/search.service';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { VisibilityConnection } from '../../../pages/search/models/user-suggestion';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { CompanyDetails } from '../../models/company-details';
import { LoggedInUser } from '../../models/logged-in-user';
import { AssigneeConnectionDetails } from '../../interfaces/company';
import { RecordVisibilityService } from '../../services/record/record-visibility.service';
import { RecordVisibilityMode } from '../../enums/record-visibility';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { AuditLogService } from '../../services/audit-log/audit-log.service';

interface SummaryEntry {
  id: number;
  name: string;
  isLead: boolean;
}

@Component({
  selector: 'app-record-visibility-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, SvgIconComponent],
  templateUrl: './record-visibility-popup.component.html',
  styleUrls: ['./record-visibility-popup.component.scss'],
})
export class RecordVisibilityPopupComponent
  extends DestroyBase
  implements OnInit
{
  @Input() recordId!: string;
  @Input() recordDetails: CompanyDetails | undefined;
  @Output() close = new EventEmitter<void>();
  @Output() visibilityUpdated = new EventEmitter<void>();

  searchService = inject(SearchService);
  authService = inject(AuthService);
  recordVisibilityService = inject(RecordVisibilityService);
  auditLogService = inject(AuditLogService);
  recordVisibilityModes = RecordVisibilityMode;
  visibilityMode: RecordVisibilityMode = RecordVisibilityMode.Public;
  visibleConnections: VisibilityConnection[] = [];
  allConnections: VisibilityConnection[] = [];
  connectionCheckboxState: Record<number, boolean> = {};
  connectionsToInclude = new Set<number>();
  connectionsToExclude = new Set<number>();
  isLoading = true;
  visibilityError: string | null = null;
  connectionSearch = '';
  currentPage = 1;
  pageSize = 3; // Keep consistent with original
  hasMoreConnections = false;
  isLoadingMore = false;
  hasLead = false;

  // Confirmation Summary
  showConfirmation = false;
  summaryAddedConnections: SummaryEntry[] = [];
  summaryRemovedConnections: SummaryEntry[] = [];
  summaryModeMessage = '';
  previousVisibilityMode: RecordVisibilityMode = RecordVisibilityMode.Public;
  nameMap = new Map<number, string>();
  originalVisibleIds = new Set<number>();

  private searchSubject = new Subject<string>();
  loggedInUserInfo: LoggedInUser | null = null;

  constructor() {
    super();
    this.searchSubject
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((searchTerm) => {
        this.connectionSearch = searchTerm;
        this.currentPage = 1;
        this.loadConnections();
      });
  }

  ngOnInit(): void {
    this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
    if (this.recordDetails?.visibility_mode) {
      this.visibilityMode = this.recordDetails.visibility_mode;
    }
    this.previousVisibilityMode =
      this.recordDetails?.visibility_mode ?? RecordVisibilityMode.Public;
    this.hasLead = this.hasLeads();
    this.loadConnections();
    this.loadVisibleConnections();
    this.updateNameMap(this.recordDetails?.assignees ?? []);
  }

  loadConnections(loadMore = false): void {
    if (!this.recordId) return;

    if (loadMore) {
      this.isLoadingMore = true;
      this.currentPage++;
    } else {
      this.isLoading = true;
    }

    this.recordVisibilityService
      .getRecordConnections(
        this.recordId,
        this.connectionSearch,
        this.currentPage,
        this.pageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (connections) => {
          const newConnections: VisibilityConnection[] =
            connections.results ?? [];
          if (this.currentPage === 1) {
            this.allConnections = newConnections;
          } else {
            this.allConnections = [...this.allConnections, ...newConnections];
          }
          this.hasMoreConnections =
            this.allConnections.length < connections.count;
          this.updateNameMap(newConnections);
          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: (err) => {
          console.error('Error loading connection data:', err);
          this.visibilityError =
            'Failed to load connection data. Please try again.';
          this.isLoading = false;
        },
      });
  }

  loadVisibleConnections(): void {
    if (!this.recordId) return;

    this.recordVisibilityService
      .getRecordVisibility(this.recordId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (visibility) => {
          this.visibleConnections = visibility ?? [];
          this.originalVisibleIds = new Set(
            this.visibleConnections.map((v) => v.id),
          );
          this.updateNameMap(this.visibleConnections);
        },
        error: (err) => {
          console.error('Error loading visibility data:', err);
          this.visibilityError =
            'Failed to load visibility data. Please try again.';
        },
      });
  }

  hasChanges(): boolean {
    // Check if there are any pending connection changes (additions or removals)
    const hasConnectionChanges =
      this.connectionsToExclude.size > 0 ||
      Object.keys(this.connectionCheckboxState).some((key) => {
        const id = Number(key);
        return (
          this.connectionCheckboxState[id] && !this.originalVisibleIds.has(id)
        );
      });

    // If target mode is Restricted, require actual connection changes
    // even if checking strictly against 'previousVisibilityMode' would suggest otherwise.
    if (this.visibilityMode === this.recordVisibilityModes.Restricted) {
      return hasConnectionChanges;
    }

    // For other modes, check if mode has changed
    return this.visibilityMode !== this.previousVisibilityMode;
  }

  onSearchConnection(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.trim();
    this.searchSubject.next(searchTerm);
  }

  onConnectionSelection(event: Event, connection_id: number): void {
    const target = event.target as HTMLInputElement;
    this.connectionCheckboxState[connection_id] = target.checked;
  }

  removeConnection(connectionId: number): void {
    this.connectionsToExclude.add(connectionId);
  }

  restoreConnection(connectionId: number): void {
    this.connectionsToExclude.delete(connectionId);
  }

  hasLeads(): boolean {
    if (
      !this.recordDetails?.assignees ||
      this.recordDetails.assignees.length === 0
    ) {
      return false;
    }
    const currentUserConnection = this.loggedInUserInfo?.connection;
    return this.recordDetails.assignees.some(
      (assignee) => assignee.connection_name !== currentUserConnection,
    );
  }

  initiateSave(): void {
    this.calculateChanges();
    this.showConfirmation = true;
  }

  calculateChanges(): void {
    // Get all currently checked IDs from state
    const checkedIds = new Set<number>();

    Object.entries(this.connectionCheckboxState).forEach(
      ([idStr, isChecked]) => {
        if (isChecked) {
          checkedIds.add(Number(idStr));
        }
      },
    );

    // Add checked ids to include
    checkedIds.forEach((id) => {
      if (!this.connectionsToInclude.has(id)) {
        this.connectionsToInclude.add(id);
      }
    });

    // Get all currently visible IDs from state
    const visibleIds = new Set(
      (this.visibleConnections ?? []).map(
        (connection: VisibilityConnection) => connection.id,
      ),
    );

    // Get all assignee ids
    const assigneeIds = this.getAssigneeIds();

    // Add assignee ids not already in visible to include
    assigneeIds.forEach((id) => {
      if (!visibleIds.has(id)) {
        this.connectionsToInclude.add(id);
      }
    });

    const connectionMap = new Map(
      this.allConnections.map((conn) => [conn.id, conn]),
    );

    // Add assignees to connectionMap with isLead = true
    this.recordDetails?.assignees?.forEach((assignee) => {
      connectionMap.set(assignee.id, { ...assignee, is_lead: true });
    });

    // Add visibleConnections to connectionMap
    this.visibleConnections.forEach((conn) => {
      connectionMap.set(conn.id, conn);
    });

    const toSummaryEntry = (id: number): SummaryEntry => {
      const connection = connectionMap.get(id);
      const fallbackName =
        connection?.display_name ||
        connection?.connection_name ||
        this.nameMap.get(id) ||
        `Connection #${id}`;

      return {
        id,
        name: fallbackName,
        isLead: connection?.is_lead || false,
      };
    };

    this.summaryAddedConnections = Array.from(this.connectionsToInclude).map(
      toSummaryEntry,
    );
    this.summaryRemovedConnections = Array.from(this.connectionsToExclude).map(
      toSummaryEntry,
    );

    this.previousVisibilityMode =
      this.recordDetails?.visibility_mode ?? RecordVisibilityMode.Public;
    if (this.visibilityMode === RecordVisibilityMode.Public) {
      this.summaryModeMessage = 'This record will be visible to all users';
    } else if (this.visibilityMode === RecordVisibilityMode.Private) {
      this.summaryModeMessage =
        'This record will be private and visible only to users of your organization.';
    } else {
      // Target is Restricted
      if (this.previousVisibilityMode === RecordVisibilityMode.Public) {
        this.summaryModeMessage =
          'Access will be restricted. Only the selected CDFIs will be able to view this record.';
      } else if (this.previousVisibilityMode === RecordVisibilityMode.Private) {
        this.summaryModeMessage =
          'Access granted to specific CDFIs. The record will no longer be private.';
      } else {
        this.summaryModeMessage =
          'Only the currently selected CDFIs will be able to view this record.';
      }
    }
  }

  cancelConfirmation(): void {
    this.showConfirmation = false;
  }

  confirmSave(): void {
    const isRestricted =
      this.visibilityMode === RecordVisibilityMode.Restricted;

    this.auditLogService.logFeature({
      featureName: 'visibility_business_record',
      action: 'update',
      meta: {
        recordId: this.recordId,
        visibilityMode: this.visibilityMode,
      },
      description: 'User updates business record visibility',
    });

    this.recordVisibilityService
      .updateRecordVisibility(this.recordId, {
        visibility_mode: this.visibilityMode,
        include_connections: isRestricted
          ? Array.from(this.connectionsToInclude)
          : [],
        exclude_connections: isRestricted
          ? Array.from(this.connectionsToExclude)
          : [],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.visibilityError = null;
          this.visibilityUpdated.emit();
          this.close.emit();
        },
        error: (error) => {
          let errorMessage = 'Failed to update visibility!';
          if (error?.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (error.error.detail) {
              errorMessage = error.error.detail;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            } else if (error.error.error) {
              errorMessage = error.error.error;
            } else if (Array.isArray(error.error)) {
              errorMessage = error.error[0];
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }
          this.visibilityError = errorMessage;
          this.showConfirmation = false; // Go back to edit on error
        },
      });
  }

  closeModal(): void {
    this.close.emit();
  }

  private updateNameMap(
    connections: AssigneeConnectionDetails[] | VisibilityConnection[],
  ): void {
    connections.forEach((connection) => {
      const name =
        connection.display_name || connection.connection_name || 'Connection';
      this.nameMap.set(connection.id ?? 0, name);
    });
  }

  private getAssigneeIds(): number[] {
    if (!this.recordDetails?.assignees) return [];
    const currentUserConnection = this.loggedInUserInfo?.connection;

    return this.recordDetails.assignees
      .filter((assignee) => assignee.connection_name !== currentUserConnection)
      .map((assignee) => assignee.id ?? 0);
  }
}
